import { useCallback, useMemo, useRef, useState, useContext } from 'react'

import {
  Avatar,
  Box,
  Menu,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Badge,
  Button,
  CircularProgress,
} from '@mui/material'
import {
  IconUser,
  IconPalette,
  IconTool,
  IconFlag,
  IconCalendarEvent,
  IconShieldCheck,
  IconClock,
  IconBolt,
  IconBubbleText,
  IconCheck,
  IconChevronRight,
  IconRocket,
} from '@tabler/icons-react'
import { useConfirm } from 'material-ui-confirm'
import { toast } from 'react-hot-toast'
import type { Assistant } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { DayContext } from 'src/lib/dayContext'
import { dayjs } from 'src/lib/dayjs'
import { isInternalUser } from 'src/lib/gates'
import { logger } from 'src/lib/logger'
import { shadows } from 'src/lib/style'

import BaseChip from '../Chips/BaseChip'

import { DELETE_ASSISTANT, UPDATE_ASSISTANT } from './mutations'
import { GET_ASSISTANT_ACTIONS } from './queries'

// Define menu item configuration for cleaner code
const menuSections = [
  {
    id: 'profile',
    items: [
      {
        id: 'basic',
        label: 'About & Instructions',
        icon: IconUser,
        tab: 'basic',
      },
      {
        id: 'style',
        label: 'Style & Personality',
        icon: IconPalette,
        tab: 'style',
      },
      {
        id: 'tools',
        label: 'Tools Configuration',
        icon: IconTool,
        tab: 'tools',
      },
      {
        id: 'goals',
        label: 'Goals & Objectives',
        icon: IconFlag,
        tab: 'goals',
      },
      {
        id: 'events',
        label: 'Event Triggers',
        icon: IconCalendarEvent,
        tab: 'events',
      },
      {
        id: 'schedules',
        label: 'Scheduled Tasks',
        icon: IconClock,
        tab: 'cron',
      },
      {
        id: 'actions',
        label: 'Action Items',
        icon: IconBolt,
        tab: 'actions',
      },
      {
        id: 'tier',
        label: 'Tier & Capabilities',
        icon: IconShieldCheck,
        tab: 'tier',
      },
    ],
  },
]

interface AssistantChipProps {
  assistant: Assistant
  showDelete?: boolean
  context?: 'chat' | 'general'
  onSetDefaultChat?: () => void
}

const AssistantChip = ({
  assistant,
  showDelete = false,
  context = 'general',
  onSetDefaultChat,
}: AssistantChipProps) => {
  const { currentUser } = useAuth()
  const { selectedWorkspace } = useContext(DayContext)
  const confirm = useConfirm()

  const [isOpen, setIsOpen] = useState(false)
  const [settingDefaultChat, setSettingDefaultChat] = useState(false)
  const anchorEl = useRef<HTMLDivElement>(null)

  // Query for assistant actions
  const { data: actionsData } = useQuery(GET_ASSISTANT_ACTIONS, {
    variables: { assistantId: assistant?.id, workspaceId: selectedWorkspace },
    skip: !assistant?.id || !selectedWorkspace || !isOpen,
  })

  // Check if this assistant is the default chat assistant
  const isDefaultChatAssistant = useMemo(() => {
    return assistant?.eventInstructions?.some(
      (instruction) =>
        instruction.eventId === 'CHAT' && instruction.defaultAssistant
    )
  }, [assistant?.eventInstructions])

  // Count active schedules
  const activeSchedulesCount = useMemo(() => {
    return (
      assistant?.schedules?.filter((schedule) => schedule.enabled).length || 0
    )
  }, [assistant?.schedules])

  // Get next action
  const nextAction = useMemo(() => {
    if (!actionsData?.actionsForAssistant?.length) return null

    const upcomingActions = actionsData.actionsForAssistant
      .filter(
        (action) =>
          action.timeframe?.reminderDate &&
          dayjs(action.timeframe.reminderDate).isAfter(dayjs())
      )
      .sort(
        (a, b) =>
          new Date(a.timeframe.reminderDate).getTime() -
          new Date(b.timeframe.reminderDate).getTime()
      )

    return upcomingActions[0] || null
  }, [actionsData])

  const totalUpcomingActions = useMemo(() => {
    return (
      actionsData?.actionsForAssistant?.filter(
        (action) =>
          action.timeframe?.reminderDate &&
          dayjs(action.timeframe.reminderDate).isAfter(dayjs())
      ).length || 0
    )
  }, [actionsData])

  const handleEditAssistant = useCallback(
    (tab?: string) => {
      // For tier-less assistants, we can't navigate to the full edit page
      // They need to complete setup first through AssistantsPage
      if (!assistant?.tierId) {
        navigate(routes.assistants())
      } else {
        const route = tab
          ? routes.assistantEdit({ assistantId: assistant?.id }) + `?tab=${tab}`
          : routes.assistantEdit({ assistantId: assistant?.id })
        navigate(route)
      }
      setIsOpen(false)
    },
    [assistant?.id, assistant?.tierId]
  )

  const [deleteAssistant, { loading: _deleteAssistantLoading }] =
    useMutation(DELETE_ASSISTANT)

  const [updateAssistant] = useMutation(UPDATE_ASSISTANT)

  const handleChatWithAssistant = useCallback(() => {
    if (!isDefaultChatAssistant && onSetDefaultChat) {
      // If not default and we have a callback, use it
      onSetDefaultChat()
    } else {
      // Otherwise just navigate to chat
      navigate(routes.thread({ threadId: 'new' }))
    }
    setIsOpen(false)
  }, [isDefaultChatAssistant, onSetDefaultChat])

  const handleSetAsDefaultChat = useCallback(async () => {
    if (isDefaultChatAssistant) return

    setSettingDefaultChat(true)

    try {
      // Find the CHAT event instruction
      const chatInstruction = assistant?.eventInstructions?.find(
        (instruction) => instruction.eventId === 'CHAT'
      )

      if (chatInstruction) {
        // Update the instruction to set as default
        const updatedInstructions = assistant.eventInstructions.map((inst) =>
          inst.eventId === 'CHAT' ? { ...inst, defaultAssistant: true } : inst
        )

        await updateAssistant({
          variables: {
            assistantId: assistant.id,
            workspaceId: selectedWorkspace,
            assistantInput: {
              eventInstructions: updatedInstructions,
            },
          },
        })

        toast.success(
          `${assistant.firstName} is now your default chat assistant!`
        )

        if (onSetDefaultChat) {
          onSetDefaultChat()
        }
      }
    } catch (error) {
      logger.error('Failed to set default chat assistant', error)
      toast.error('Failed to update default assistant')
    } finally {
      setSettingDefaultChat(false)
      setIsOpen(false)
    }
  }, [
    assistant,
    isDefaultChatAssistant,
    selectedWorkspace,
    updateAssistant,
    onSetDefaultChat,
  ])

  const handleDeleteAssistant = useCallback(
    async ({
      workspaceId,
      assistantId,
    }: {
      workspaceId: string
      assistantId: string
    }) => {
      if (!showDelete) {
        return
      }

      try {
        await confirm({
          title: 'Are you sure you want to delete this assistant?',
          description:
            'This action cannot be undone. This will remove the assistant from the workspace and will have significant impact on the owner. If you are *absolutely sure* you want to delete this assistant, type "ABSOLUTELY SURE" to proceed.',
          confirmationKeyword: 'ABSOLUTELY SURE',
        })
      } catch (error) {
        logger.error('User cancelled delete assistant', error)
        return
      }
      toast.promise(
        deleteAssistant({
          variables: { workspaceId, assistantId },
        }),
        {
          loading: 'Deleting assistant...',
          success: () => {
            return 'Assistant deleted successfully!'
          },
          error: (error) => {
            logger.error('Failed to delete assistant', error)
            return 'Failed to delete assistant: ' + error.message
          },
        }
      )
    },
    [deleteAssistant, confirm, showDelete]
  )

  const internalAdmin = useMemo(
    () => isInternalUser(currentUser),
    [currentUser]
  )

  // Style definitions for the beautiful menu
  const menuStyles = {
    paper: {
      minWidth: '380px',
      maxWidth: '420px',
      borderRadius: '12px',
      boxShadow: (theme) => shadows[theme.palette.mode].md,
      overflow: 'hidden',
      border: (theme) => `1px solid ${theme.palette.divider}`,
    },
  }

  const profileHeaderSx = {
    backgroundColor: (theme) => theme.palette.background.paper,
    p: 3,
    position: 'relative',
    overflow: 'hidden',
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  }

  const avatarContainerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    position: 'relative',
    zIndex: 1,
  }

  const menuContentSx = {
    px: 2,
    py: 1,
  }

  const statusSectionSx = {
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap',
    my: 2,
    px: 1,
  }

  const statusChipSx = {
    borderRadius: '16px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: (theme) => shadows[theme.palette.mode].sm,
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  }

  const menuItemSx = {
    borderRadius: '8px',
    mx: 0.5,
    my: 0.25,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  }

  const chatButtonSx = {
    width: '100%',
    borderRadius: '8px',
    mb: 1,
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: (theme) => theme.palette.background.paper,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    color: (theme) => theme.palette.text.primary,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
      transform: 'translateY(-1px)',
      boxShadow: (theme) => shadows[theme.palette.mode].sm,
    },
    transition: 'all 0.2s ease',
  }

  const defaultChatButtonSx = {
    width: '100%',
    mb: 2,
    borderRadius: '8px',
    textTransform: 'none',
    borderStyle: 'dashed',
    borderWidth: '2px',
    py: 1,
    '&:hover': {
      borderStyle: 'solid',
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  }

  return (
    assistant && (
      <>
        <Box ref={anchorEl}>
          <BaseChip
            label={`${assistant.firstName} ${assistant.lastName}`.trim()}
            icon={<Avatar src={assistant.photoUrl} />}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setIsOpen(true)
            }}
            onRemove={
              internalAdmin && showDelete
                ? async () =>
                    await handleDeleteAssistant({
                      workspaceId: assistant.workspaceId,
                      assistantId: assistant.id,
                    })
                : undefined
            }
            sx={{
              '&.MuiChip-root.day-ai-clickable-chip .MuiChip-icon': {
                ml: 0,
              },
            }}
          />
        </Box>
        <Menu
          open={isOpen}
          anchorEl={anchorEl.current}
          onClose={() => setIsOpen(false)}
          slotProps={{
            paper: {
              sx: menuStyles.paper,
            },
          }}
        >
          {/* Profile Header Section */}
          <Box sx={profileHeaderSx}>
            <Box sx={avatarContainerSx}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              >
                <Avatar
                  src={assistant.photoUrl}
                  alt={`${assistant.firstName} ${assistant.lastName}`}
                  sx={{
                    width: 72,
                    height: 72,
                  }}
                />
              </Badge>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '20px',
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {assistant.firstName} {assistant.lastName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: '14px',
                  }}
                >
                  {assistant.title ||
                    `${assistant.tier?.name ? `${assistant.tier.name} Tier` : 'Assistant'}`}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Chat Button Section */}
          <Box sx={menuContentSx}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleChatWithAssistant}
              startIcon={<IconBubbleText size={16} />}
              sx={chatButtonSx}
            >
              Chat with {assistant.firstName}
            </Button>

            {/* Default Chat Assistant Toggle */}
            {context === 'chat' && !isDefaultChatAssistant && (
              <Button
                variant="outlined"
                onClick={handleSetAsDefaultChat}
                disabled={settingDefaultChat}
                startIcon={
                  settingDefaultChat ? (
                    <CircularProgress size={16} />
                  ) : (
                    <IconRocket size={16} />
                  )
                }
                sx={defaultChatButtonSx}
              >
                Set as Default Chat Assistant
              </Button>
            )}
          </Box>

          {/* Status Section */}
          <Box sx={statusSectionSx}>
            {assistant.tier && (
              <Chip
                label={assistant.tier.name}
                size="small"
                icon={<IconShieldCheck size={14} />}
                variant="outlined"
                sx={statusChipSx}
                onClick={() => handleEditAssistant('tier')}
              />
            )}

            {activeSchedulesCount > 0 && (
              <Chip
                label={`${activeSchedulesCount} Active Schedule${activeSchedulesCount !== 1 ? 's' : ''}`}
                size="small"
                icon={<IconClock size={14} />}
                variant="outlined"
                sx={statusChipSx}
                onClick={() => handleEditAssistant('cron')}
              />
            )}

            {nextAction && (
              <Chip
                label={`Next: ${dayjs(nextAction.timeframe.reminderDate).fromNow()}`}
                size="small"
                icon={<IconBolt size={14} />}
                variant="outlined"
                sx={statusChipSx}
                onClick={() => handleEditAssistant('actions')}
              />
            )}

            {totalUpcomingActions > 0 && (
              <Chip
                label={`${totalUpcomingActions} Upcoming Action${totalUpcomingActions !== 1 ? 's' : ''}`}
                size="small"
                icon={<IconCheck size={14} />}
                variant="outlined"
                sx={statusChipSx}
                onClick={() => handleEditAssistant('actions')}
              />
            )}
          </Box>

          {/* Menu Items */}
          <Box sx={{ pb: 1 }}>
            {menuSections[0].items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleEditAssistant(item.tab)}
                sx={menuItemSx}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <item.icon size={18} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
                <IconChevronRight
                  size={16}
                  color="action"
                />
              </ListItemButton>
            ))}
          </Box>
        </Menu>
      </>
    )
  )
}

export default AssistantChip
